import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form } from '@unform/web';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO, addDays, subDays } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
// icons
import {
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiMessageSquare,
  FiArrowLeft,
  FiCheckCircle,
} from 'react-icons/fi';

// servicos
import api from 'services/api';

// actions
import { show } from 'store/modules/dialog/actions';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import NoSearch2 from 'assets/nosearch2.svg';

// componentes
import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import Flag from 'components/Flag';
import SelectDate from 'components/SelectDate';
import NotFound from 'components/NotFound';

// estilos
import { Container, Table } from './styles';
import { Scroll, Td } from 'styles/global';
import { EnviaExibidor, Midia } from 'pages/@types';
import Loader from 'components/ContentLoader/GenericLoader';
import { Avaliacaoeng, Role, StatusEnviaExibidor } from 'utils/variables';
import AppError from 'utils/AppError';
import { downloadFile } from 'utils/index';
import DropDownMenu from 'components/DropDownMenu';

export default function MidiaAvulsa() {
  /*
   * REDUX
   */
  const dispatch = useDispatch();
  const { currentDate, emissora, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * VARIAVEIS
   */
  const [loading, setLoading] = useState(false);
  const [midias, setMidias] = useState<Midia[]>([]);
  const [filtro, setFiltro] = useState('');
  const [selected, setSelected] = useState<Midia | null>(null);
  const [loadingDownload, setLoadingDownload] = useState(false);

  const midiasFiltradas = midias.filter(m =>
    m.filename.includes(filtro.toUpperCase()),
  );
  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);
  const navigate = useNavigate();

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  /**
   * USE CALLBACK
   */
  const handleDownload = useCallback(async (url, filename) => {
    setLoadingDownload(true);
    try {
      toast.info('Iniciando download...');
      await downloadFile({
        url,
        name: filename,
        fromAPI: true,
      });
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        console.log(appError);
        toast.error('Falha ao baixar o arquivo. Tente novamente mais tarde');
      }
    } finally {
      setLoadingDownload(false);
    }
  }, []);

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleReenviar = useCallback(
    (midia, envioId) => {
      const options = {
        type: 'question',
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/enviaexibidor/${envioId}`,
          method: api.patch,
          data: { status: 'pendente' },
          action: 'reenvio',
        },
        haveCallback: true,
        successMessage: 'Status alterado!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
        contentHTML: `<p>Iniciar um novo envio do material <b>${midia.filename}</b> para o exibidor ?</p>`,
      };

      dispatch(show(options));
    },
    [dispatch],
  );

  const handleGravacao = useCallback(
    (midia, envioId) => {
      const options = {
        type: 'question',
        callback: {
          url: `/enviaexibidor/${envioId}`,
          method: api.patch,
          data: { status: 'ingestmanual' },
          action: 'ingestmanual',
        },
        confirmButtonText: 'CONFIRMAR',
        haveCallback: true,
        successMessage: 'Gravação sinalizada!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
        contentHTML: `<p>Confirma a gravação de <b>${midia.filename}</b>?</p>`,
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  const handleRessalva = useCallback(
    midia => {
      const contentHTML = `<p>Observação: ${midia.obseng}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  function handleActionSelect(m: Midia, action: { value: number }) {
    setSelected(m);
    const envioId = m.enviaexibidor.find(
      (e: EnviaExibidor) => e.emissora === emissora.value,
    )?.id;
    switch (action.value) {
      case 1:
        handleGravacao(m, envioId);
        break;
      case 2:
        handleReenviar(m, envioId);
        break;
      case 3:
        handleDownload(m.download_url, m.filename);
        break;
      default:
        break;
    }
  }

  /**
   * USEEFFECT
   */

  useEffect(() => {
    const controller = new AbortController();
    async function buscaMidiasAvulsas() {
      setLoading(true);
      try {
        const response = await api.get('/midia/midiasavulsas', {
          signal: controller.signal,
          params: {
            date: format(new Date(currentDate), 'yyyy-MM-dd'),
            emissora_id: emissora.value,
          },
        });
        if (response && response.data) setMidias(response.data);
        else toast.error('Falha de comunicação com o sistema');

        setLoading(false);
      } catch (e) {
        setLoading(false);
        if (e instanceof Error) toast.error(e.message);
        else toast.error('Erro inesperado');
      }
    }
    buscaMidiasAvulsas();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  useEffect(() => {
    if (
      selected &&
      callbackDialog &&
      callbackDialog.success &&
      (callbackDialog.success.action === 'reenvio' ||
        callbackDialog.success.action === 'ingestmanual')
    ) {
      setMidias(prevState =>
        prevState.map(m =>
          m.id === selected.id
            ? {
                ...m,
                enviaexibidor: m.enviaexibidor.map(e =>
                  e.id === callbackDialog.success.data.id
                    ? {
                        ...e,
                        statusgeral: callbackDialog.success.data.statusgeral,
                      }
                    : e,
                ),
              }
            : m,
        ),
      );
    }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <nav>
        <Button size="lg" color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
        {dateFormatted && new Date(currentDate) && (
          <SelectDate
            dateFormatted={dateFormatted}
            currentDateZoned={String(new Date(currentDate))}
            handleNextDay={handleNextDay}
            handleDateChange={handleDateChange}
            handlePrevDay={handlePrevDay}
          />
        )}
        <div />
      </nav>
      <Card>
        <header>
          <h4>LISTA DE MIDIAS</h4>
          <div>
            <Form onSubmit={() => {}}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
                onChange={e => setFiltro(e.target.value)}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : midiasFiltradas && midiasFiltradas.length ? (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>FILENAME</th>
                  <th>CATEGORIA</th>
                  <th>STATUS CC</th>
                  <th>STATUS</th>
                  <th>DATA DE CRIAÇÃO</th>
                  <th>OBS.</th>
                  <th>AÇÕES</th>
                </tr>
              </thead>

              <tbody>
                {midiasFiltradas.map((midia: Midia, index) => {
                  const envioFiltered = midia.enviaexibidor?.find?.(
                    e => e.emissora === emissora.value,
                  );
                  return (
                    <tr key={midia.id}>
                      <Td>{midia.id}</Td>
                      <Td>{midia.filename}</Td>
                      <Td>
                        <Flag name={midia.categoria} type={midia.categoria} />
                      </Td>
                      <Td>
                        <Flag
                          name={midia.statuscaption}
                          type={midia.statuscaption}
                        />
                      </Td>
                      <Td>
                        <Flag
                          type={envioFiltered?.statusgeral.type || 'NA'}
                          name={envioFiltered?.statusgeral.nome || 'NA'}
                          tooltip={envioFiltered?.statusgeral.info || 'NA'}
                        />
                      </Td>
                      <Td>
                        {format(
                          parseISO(midia.created_at),
                          "dd 'de' MMMM', às' H:mm'	",
                          {
                            locale: pt,
                          },
                        )}
                      </Td>
                      <Td>
                        {midia && midia.obseng && (
                          <Button
                            fill
                            color="font"
                            round
                            onClick={() => handleRessalva(midia)}
                          >
                            <FiMessageSquare />
                          </Button>
                        )}
                      </Td>
                      <Td>
                        <DropDownMenu
                          disabled={false}
                          id={`acoes${index}`}
                          actions={[
                            {
                              value: 1,
                              label: 'GRAVADO',
                              icon: <FiCheckCircle />,
                              disabled:
                                envioFiltered?.status ===
                                  StatusEnviaExibidor.CONCLUIDO ||
                                envioFiltered?.status ===
                                  StatusEnviaExibidor.INGESTMANUAL ||
                                midia.avaliacaoeng === Avaliacaoeng.REPROVADO,
                            },
                            {
                              value: 2,
                              label: 'REENVIAR',
                              icon: <FiRefreshCw />,
                              tooltip: midia.filedeleted_at
                                ? 'MÍDIA VENCIDA'
                                : undefined,
                              disabled:
                                !!midia.filedeleted_at ||
                                midia.avaliacaoeng === Avaliacaoeng.REPROVADO,
                            },
                            {
                              value: 3,
                              label: 'DOWNLOAD',
                              icon: <FiDownload />,
                              tooltip: midia.filedeleted_at
                                ? 'MÍDIA VENCIDA'
                                : undefined,
                              disabled:
                                loadingDownload ||
                                !!midia.filedeleted_at ||
                                (profile.role !== Role.ENG &&
                                  profile.role !== Role.ADMIN),
                            },
                          ]}
                          handleClick={action =>
                            !action.disabled &&
                            handleActionSelect(midia, action)
                          }
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroll>
        ) : (
          <NotFound img={NoSearch2} title="Mídias Avulsas">
            Nenhuma MÍDIA AVULSA encontrada
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
